import { useState } from 'react';
import { ExternalLink, FileText, ChevronRight } from 'lucide-react';
import type { DetailRow, PaneData } from '../../shared/contracts';
import { formatNumber } from '../../shared/format';
import { detailRowsOf } from '../lib/pane-rows';
import { Modal } from './Modal';
import { ComparisonChart } from './charts';
import { DrillTable } from './DrillTable';
import { TicketThread } from './TicketThread';
import type { TableRow } from '../../shared/contracts';
import { EmptyRow } from './States';

/** One categorical slice of a pane (breakdown / stacked month / trend point). */
interface Bucket {
  key: string;
  label: string;
  value: number;
}

/** The pane's categorical dimension, if any — never synthesised into fake records. */
function bucketsOf(pane: PaneData): Bucket[] {
  const { breakdown, stacked, metric } = pane;
  if (breakdown?.length) return breakdown.map((b) => ({ key: b.label, label: b.label, value: b.value }));
  if (stacked?.series.length) return stacked.months.map((m, i) => ({ key: m, label: m, value: stacked.total[i] ?? 0 }));
  const trend = metric?.trend;
  if (trend && trend.length > 1) return trend.map((t) => ({ key: t.label, label: t.label, value: t.value }));
  return [];
}


/**
 * Drill-down modal. Three honest levels:
 *  - flat panes (rows, no categories) render their real records directly;
 *  - categorical panes show a clickable category list (level 2) that drills to the real
 *    rows behind a category (level 3), with breadcrumb-back navigation;
 *  - when the source genuinely cannot expose individual rows, the most detailed aggregate
 *    is shown CLEARLY LABELLED as aggregate — it is never dressed up as fake records.
 * Active period/filters are preserved because the rows are the ones the pane was loaded with.
 */
export function DrillModal({
  pane,
  bucket,
  onClose,
  onOpslag,
}: {
  pane: PaneData;
  bucket?: string;
  onClose: () => void;
  onOpslag: (row: DetailRow) => void;
}) {
  const [activeBucket, setActiveBucket] = useState<string | undefined>(bucket);
  const [threadRow, setThreadRow] = useState<TableRow | null>(null);
  // Lifted so the closed-filter survives the table unmounting on the thread round trip.
  const [hideClosed, setHideClosed] = useState(false);
  const details = detailRowsOf(pane);
  const buckets = bucketsOf(pane);
  const hasBuckets = buckets.length > 0;
  const drillable = details.some((r) => r.bucket != null);
  const table = pane.table;

  const bucketRows = activeBucket != null ? details.filter((r) => r.bucket === activeBucket) : [];
  const activeBucketValue = activeBucket != null ? buckets.find((b) => b.key === activeBucket)?.value : undefined;
  // A chart-bucket click on a table pane pre-filters the table by group. The group
  // toggle is dropped with it — grouping a single-group view is meaningless.
  const tableView = table
    ? activeBucket != null
      ? { ...table, rows: table.rows.filter((r) => r.group === activeBucket), groupLabel: undefined }
      : table
    : null;

  // Tables and chat threads need room; category/detail lists stay compact.
  const wide = threadRow != null || Boolean(tableView && tableView.rows.length > 0);

  return (
    <Modal title={pane.title} subtitle={pane.source.label} onClose={onClose} wide={wide}>
      <nav className="mb-3 flex items-center gap-1 text-xs text-brand-dark/50">
        <button
          type="button"
          onClick={() => {
            setThreadRow(null);
            setActiveBucket(undefined);
          }}
          disabled={activeBucket == null && threadRow == null}
          className={activeBucket != null || threadRow != null ? 'rounded px-1 py-0.5 hover:text-brand-primary hover:underline' : 'cursor-default px-1 py-0.5'}
        >
          Datagrundlag
        </button>
        {activeBucket != null && (
          <>
            <ChevronRight size={12} className="shrink-0" />
            <button
              type="button"
              onClick={() => setThreadRow(null)}
              disabled={threadRow == null}
              className={threadRow != null ? 'truncate rounded px-1 py-0.5 font-medium text-brand-dark/70 hover:text-brand-primary hover:underline' : 'cursor-default truncate px-1 py-0.5 font-medium text-brand-dark/70'}
            >
              {activeBucket}
            </button>
          </>
        )}
        {threadRow != null && (
          <>
            <ChevronRight size={12} className="shrink-0" />
            <span className="truncate font-medium text-brand-dark/70">Korrespondance</span>
          </>
        )}
      </nav>

      {threadRow?.drill ? (
        <TicketThread ticketId={threadRow.drill.id} href={threadRow.href} />
      ) : tableView ? (
        <>
          {activeBucket == null && pane.comparison && <ComparisonChart data={pane.comparison} />}
          {tableView.rows.length ? (
            <DrillTable key={activeBucket ?? '__all'} table={tableView} onDrillRow={setThreadRow} hideClosed={hideClosed} onHideClosedChange={setHideClosed} />
          ) : (
            <AggregateOnly label={activeBucket ?? pane.title} value={activeBucketValue} source={pane.source.label} />
          )}
        </>
      ) : (
        <>
          {activeBucket == null && pane.comparison && <ComparisonChart data={pane.comparison} />}
          {activeBucket != null ? (
            bucketRows.length ? (
              <DetailList key={activeBucket} rows={bucketRows} onOpslag={onOpslag} />
            ) : (
              <AggregateOnly label={activeBucket} value={activeBucketValue} source={pane.source.label} />
            )
          ) : hasBuckets ? (
            <BucketList buckets={buckets} drillable={drillable} onPick={setActiveBucket} />
          ) : details.length ? (
            <DetailList key="__all" rows={details} onOpslag={onOpslag} />
          ) : (
            <ul>
              <EmptyRow label="Ingen rækker i datasættet." />
            </ul>
          )}
        </>
      )}
    </Modal>
  );
}

/** Rows rendered per "Vis flere" click — large sets stay scannable and cheap to mount. */
const DETAIL_PAGE_SIZE = 50;

/**
 * Real source records — the only place hrefs/opslag affordances appear. Long sets are
 * paged client-side with an explicit "Viser X af Y rækker" counter, never silently cut.
 */
function DetailList({ rows, onOpslag }: { rows: DetailRow[]; onOpslag: (row: DetailRow) => void }) {
  const [limit, setLimit] = useState(DETAIL_PAGE_SIZE);
  const visible = rows.length > limit ? rows.slice(0, limit) : rows;
  return (
    <div>
      <ul className="divide-y divide-brand-primary/5">
        {visible.map((r) => (
        <li key={r.id} className="flex items-center gap-3 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-brand-dark">{r.title}</div>
            {r.subtitle && <div className="truncate text-xs text-brand-dark/50">{r.subtitle}</div>}
          </div>
          {r.value && <div className="whitespace-nowrap text-sm font-semibold text-brand-primary">{r.value}</div>}
          {r.meta && <div className="whitespace-nowrap text-[11px] text-brand-dark/45">{r.meta}</div>}
          {r.html ? (
            <button type="button" onClick={() => onOpslag(r)} className="text-brand-accent hover:text-brand-accent-hover" title="Vis indhold">
              <FileText size={15} />
            </button>
          ) : r.href ? (
            <a href={r.href} target="_blank" rel="noreferrer" className="text-brand-accent hover:text-brand-accent-hover" title="Åbn i kildesystem">
              <ExternalLink size={15} />
            </a>
          ) : null}
        </li>
      ))}
      </ul>
      {rows.length > visible.length && (
        <div className="mt-2 flex items-center justify-between border-t border-brand-primary/10 pt-2">
          <span className="text-xs text-brand-dark/50">
            Viser {visible.length} af {rows.length} rækker
          </span>
          <button
            type="button"
            onClick={() => setLimit((l) => l + DETAIL_PAGE_SIZE)}
            className="rounded px-2 py-1 text-xs font-medium text-brand-accent hover:bg-brand-primary/5 hover:text-brand-accent-hover"
          >
            Vis flere
          </button>
        </div>
      )}
    </div>
  );
}

/** Level-2 categories — each drills into its rows (or its labelled aggregate). */
function BucketList({ buckets, drillable, onPick }: { buckets: Bucket[]; drillable: boolean; onPick: (key: string) => void }) {
  return (
    <div>
      {!drillable && (
        <p className="mb-2 rounded-md bg-brand-amber-light/40 px-3 py-2 text-xs text-brand-dark/60">
          Kun aggregeret data — kilden kan ikke vise de enkelte rækker bag tallene.
        </p>
      )}
      <ul className="divide-y divide-brand-primary/5">
        {buckets.map((b) => (
          <li key={b.key}>
            <button
              type="button"
              onClick={() => onPick(b.key)}
              className="-mx-2 flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-brand-primary/5"
            >
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-brand-dark">{b.label}</span>
              <span className="whitespace-nowrap text-sm font-semibold text-brand-primary">{formatNumber(b.value)}</span>
              <ChevronRight size={15} className="shrink-0 text-brand-dark/30" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The most detailed view a source can give when it has no per-row data — clearly labelled. */
function AggregateOnly({ label, value, source }: { label: string; value?: number; source: string }) {
  return (
    <div className="rounded-md bg-brand-amber-light/40 px-4 py-5 text-center">
      <p className="text-sm font-medium text-brand-dark">{label}</p>
      <p className="mt-1 font-heading text-2xl font-bold text-brand-primary">{value != null ? formatNumber(value) : '—'}</p>
      <p className="mt-2 text-xs text-brand-dark/55">
        Kun aggregeret data tilgængelig — {source} kan ikke vise de enkelte rækker bag dette tal.
      </p>
    </div>
  );
}
